import { createContext, useContext, useEffect, useState } from 'react';

import { getAppContent } from '../api/appContent';

import { DEFAULT_USER } from '../utils/constants';

import NotAvailable from '../pages/NotAvailable/NotAvailable';
import { imageCacheService } from '../services/preloadService';

import { generateColors } from '../styles/generateColors';
import { getAllSubscriptionProducts, getSubscriptionProducts } from '../api/subscription';

// Create context
const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const telegram = window.Telegram?.WebApp;
  const user = telegram ? telegram.initDataUnsafe?.user : DEFAULT_USER;

  const [entranceInfo, setEntranceInfo] = useState(null);
  const [error, setError] = useState(false);
  const [appDescription, setAppDescription] = useState(null);
  const [appContent, setAppContent] = useState(null);
  const [appSubscriptionInfo, setAppSubscriptionInfo] = useState(null);
  const [criticalFiles, setCriticalFiles] = useState([]);
  const [nonCriticalFiles, setNonCriticalFiles] = useState([]);

  const [appDataLoaded, setAppDataLoaded] = useState(false);
  const [criticalFilesLoadedProgress, setCriticalFilesLoadedProgress] = useState(0);
  const [basicDataLoaded, setBasicDataLoaded] = useState(false);
  const [allSubscriptionProducts, setAllSubscriptionProducts] = useState([]);

  useEffect(() => {
    const getAppData = async () => {
      const result = await getAppContent(window.APP_ID);
      const subscriptionProducts = await getAllSubscriptionProducts(window.APP_ID);

      setAllSubscriptionProducts(subscriptionProducts);

      if (
        !result ||
        !result?.app_description ||
        !result?.app_content ||
        !result?.app_description?.agreements?.offer ||
        !result?.app_description?.agreements?.privacy ||
        !result?.app_description?.agreements?.terms ||
        !result?.app_description?.bot_url ||
        !result?.app_description?.mini_app_url
      ) {
        setError(true);
      } else {
        generateColors(result?.app_description?.color_theme || '255, 104, 180');
        // Собираем критические файлы
        const critical = [
          result.app_description.avatar || '',
          result.app_description.agreements?.offer || '',
          result.app_description.agreements?.privacy || '',
          result.app_description.agreements?.terms || '',
          ...(result.app_content?.guides?.map(guide => guide.image) || ''),
          ...(result.app_content?.posts?.map(post => post.image) || ''),
          ...(result.app_content?.stories?.map(story => story.image) || ''),
        ];

        // Собираем некритические файлы
        const nonCritical = [
          result?.app_subscription?.logo || '',
          result?.app_subscription?.subscribeText || '',
          ...(result.app_description.communities?.map(community => community.image) || ''),
          ...(result.app_content?.stories?.flatMap(story => story.content.map(item => item.image).filter(Boolean)) ||
            ''),
          ...(result.app_content?.guides?.map(guide => guide.product) || ''),
        ].filter(Boolean);

        setCriticalFiles(critical);
        setNonCriticalFiles(nonCritical);

        setAppDescription({
          ...result.app_description,
          communities: [...(result.app_description.communities || [])].sort((a, b) => a.order - b.order),
          links: [...(result.app_description.links || [])].sort((a, b) => a.order - b.order),
          stats: [...(result.app_description.stats || [])].sort((a, b) => a.order - b.order),
        });
        setAppContent({
          ...result.app_content,
          guides: [...(result.app_content.guides || [])].sort((a, b) => a.order - b.order),
          posts: [...(result.app_content.posts || [])].sort((a, b) => a.order - b.order),
          products: [...(result.app_content.products || [])].sort((a, b) => a.order - b.order),
          stories: [...(result.app_content.stories || [])]
            .sort((a, b) => a.order - b.order)
            .map(story => ({
              ...story,
              content: [...story.content].sort((a, b) => a.order - b.order),
            })),
        });
        setAppSubscriptionInfo(result?.app_subscription || null);
        setAppDataLoaded(true);
      }
    };

    getAppData();
  }, []);

  // Отдельный useEffect для загрузки критических изображений
  useEffect(() => {
    if (!!appContent) {
      const initializeCriticalImages = async () => {
        try {
          await imageCacheService.preloadCriticalImages(criticalFiles, progress => {
            setCriticalFilesLoadedProgress(Math.round(progress));
          });
          imageCacheService.preloadNonCriticalImages(nonCriticalFiles);
        } catch (error) {
          console.warn('Image loading error:', error);
        }
      };

      initializeCriticalImages();
    }
  }, [appContent, criticalFiles, nonCriticalFiles]);

  useEffect(() => {
    if (appDataLoaded && criticalFilesLoadedProgress === 100) {
      setBasicDataLoaded(true);
    }
  }, [appDataLoaded, criticalFilesLoadedProgress]);

  // Provide this context value to all children
  const value = {
    user,
    telegram,
    entranceInfo,
    setEntranceInfo,
    appDescription,
    appContent,
    appSubscriptionInfo,
    criticalFilesLoadedProgress,
    basicDataLoaded,
    allSubscriptionProducts,
  };

  if (error) {
    return <NotAvailable />;
  }

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

// Custom hook for using this context
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    return {
      user: null,
      telegram: null,
      entranceInfo: null,
      setEntranceInfo: () => {},
      appDescription: null,
      appContent: null,
      appSubscriptionInfo: null,
      basicDataLoaded: false,
    };
  }
  return context;
};
